import {
   Box,
   Badge,
   Button,
   ButtonBase,
   Chip,
   Divider,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem,
   Stack,
   Toolbar,
   Typography,
} from '@mui/material';
import {useState} from 'react';
import {NavLink, useLocation} from 'react-router';
import {useAtomValue, useSetAtom} from 'jotai';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SyncIcon from '@mui/icons-material/Sync';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {authStatusAtom, cachedSessionAtom, isOnlineAtom, signOutAtom} from '@/state/authAtoms';
import {offlineQueueAtom, syncStateAtom} from '@/state/offlineQueue';
import {type ThemeModePreference, themeModePreferenceAtom} from '@/state/themeAtom';
import {useDevice} from '@/hooks/useDevice.ts';
import ResponsiveDatePicker from '@/components/ResponsiveDatePicker.tsx';
import {reportDateAtom} from '@/state/commonAtoms.ts';
import {routerLinks} from '@/Constants.ts';
import {RoleType} from '@/types/types.ts';
import {getInitials} from '@/lib/util.ts';
import {needsReviewCountAtom} from '@/state/runTicketAtoms';

const NAV_ITEMS = [
   {label: 'Wells', path: routerLinks.wells},
   {label: 'Barrel Tests', path: routerLinks.barrelTests},
   {label: 'Run Tickets', path: routerLinks.runTickets},
   {label: 'Pulled Bottoms', path: routerLinks.pulledBottoms},
];

// Split around the Reports dropdown (rendered separately, between these
// two) rather than one flat admin list — Reports/Audit Report/Tanks
// Allocations Report used to be three separate top-level buttons, which
// is exactly the "too many flat nav items" problem this groups away. See
// REPORTS_MENU_ITEMS below.
const ADMIN_NAV_ITEMS_MAIN = [
   {label: 'Dashboard', path: routerLinks.dashboard},
   {label: 'Wells', path: routerLinks.wells},
   {label: 'Barrel Tests', path: routerLinks.barrelTests},
   {label: 'Run Tickets', path: routerLinks.runTickets},
   {label: 'Pulled Bottoms', path: routerLinks.pulledBottoms},
];

const REPORTS_MENU_ITEMS = [
   {label: 'Reports', path: routerLinks.reports},
   {label: 'Tanks Allocations Report', path: routerLinks.tanksAllocationsReport},
   {label: 'Production Report', path: routerLinks.productionReport},
   {label: 'OGP03I Export', path: routerLinks.ogp03iExport},
   {label: 'Audit Report', path: routerLinks.auditReport},
];

// Same grouping treatment as Reports above — Users/Wells Setup used to be
// two more flat top-level buttons.
const SETUP_MENU_ITEMS = [
   {label: 'Users', path: routerLinks.userListSetup},
   {label: 'Wells Setup', path: routerLinks.wellListSetup},
   {label: 'Leases Setup', path: routerLinks.leaseListSetup},
   // Price Config removed per Duane, 2026-08-28 — its only purpose was
   // feeding revenue, which the client had removed the same day (see
   // ReportsPage.tsx/DashboardPage.tsx's own "removed per the client,
   // 2026-08-28" comments). Route/page left in place, just unreachable
   // from nav — see AppRoutes.tsx.
   // {label: 'Price Config', path: routerLinks.priceConfigSetup},
];

const THEME_OPTIONS: {value: ThemeModePreference; label: string; icon: JSX.Element}[] = [
   {value: 'light', label: 'Light', icon: <LightModeIcon fontSize='small' />},
   {value: 'dark', label: 'Dark', icon: <DarkModeIcon fontSize='small' />},
   {value: 'system', label: 'System', icon: <SettingsBrightnessIcon fontSize='small' />},
];

export function TopNav() {
   const status = useAtomValue(authStatusAtom);
   const isOnline = useAtomValue(isOnlineAtom);
   const cachedSession = useAtomValue(cachedSessionAtom);
   const queue = useAtomValue(offlineQueueAtom);
   const syncState = useAtomValue(syncStateAtom);
   const needsReviewCount = useAtomValue(needsReviewCountAtom);
   const signOut = useSetAtom(signOutAtom);
   const themePreference = useAtomValue(themeModePreferenceAtom);
   const setThemePreference = useSetAtom(themeModePreferenceAtom);
   const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
   const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null);
   const [setupMenuAnchor, setSetupMenuAnchor] = useState<null | HTMLElement>(null);
   const device = useDevice({mobileBreakpoint: 1070});
   const location = useLocation();

   const firstName = cachedSession?.firstName?.trim();
   const lastName = cachedSession?.lastName?.trim();
   const fullName = [firstName, lastName].filter(Boolean).join(' ');
   const displayName = fullName || cachedSession?.username || 'Inactive user';

   const isAdmin = cachedSession?.role === RoleType.Admin || cachedSession?.role === RoleType.SuperAdmin;
   const navItems = isAdmin ? ADMIN_NAV_ITEMS_MAIN : NAV_ITEMS;

   const initials = getInitials(cachedSession);

   // FIXED 2026-08-28 (Duane, reported on BottomNav.tsx's mobile Badge —
   // see that component's identical comment for the full explanation):
   // `syncStateAtom` can read 'syncing' while `isOnlineAtom` is stale-true
   // (the device's network interface looks connected but has no real
   // reachability, which the OS-level "No internet connection" banner
   // catches but `navigator.onLine` cannot) — so the pending-sync Chip
   // below could show a spinning icon and "Syncing N…" text at the same
   // moment the Offline Chip right next to it says there's no
   // connection. Same fix: never DISPLAY the syncing state on a render
   // where this component is also showing "Offline" — show the plain
   // pending count instead. The real background retry is unaffected.
   const isSyncing = syncState === 'syncing' && isOnline;

   // Shared renderer for the Reports/Setup nav dropdowns — a button that
   // opens a Menu of routes, highlighted whenever the current page is one
   // of its items. Add more groups here the same way if the flat nav
   // count creeps back up.
   const renderNavDropdown = (
      label: string,
      items: {label: string; path: string}[],
      anchor: null | HTMLElement,
      setAnchor: (el: null | HTMLElement) => void,
   ) => {
      const isActive = items.some((item) => location.pathname === item.path);
      return (
         <>
            <Button
               size={device.isSmallViewport ? 'small' : 'medium'}
               onClick={(e) => setAnchor(e.currentTarget)}
               endIcon={<KeyboardArrowDownIcon fontSize='small' />}
               sx={{
                  color: 'text.secondary',
                  px: {xs: 0.5, sm: 1, md: 2},
                  ...(isActive && {bgcolor: 'rgba(225,90,44,0.1)', color: 'primary.main'}),
               }}
            >
               {label}
            </Button>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
               {items.map((item) => (
                  <MenuItem
                     key={item.path}
                     component={NavLink}
                     to={item.path}
                     selected={location.pathname === item.path}
                     onClick={() => setAnchor(null)}
                  >
                     {item.label}
                  </MenuItem>
               ))}
            </Menu>
         </>
      );
   };

   const renderNavButton = (item: {label: string; path: string}) => {
      const button = (
         <Button
            key={item.path}
            size={device.isSmallViewport ? 'small' : 'medium'}
            component={NavLink}
            to={item.path}
            sx={{
               color: 'text.secondary',
               px: {xs: 0.5, sm: 1, md: 2},
               '&.active': {
                  bgcolor: 'rgba(225,90,44,0.1)',
                  color: 'primary.main',
               },
            }}
         >
            {item.label}
         </Button>
      );
      // Durable "N tickets awaiting review" indicator — see
      // needsReviewCountAtom (state/runTicketAtoms.ts). Only Run Tickets
      // carries a badge; no other nav item has a count worth surfacing
      // this way.
      if (item.path === routerLinks.runTickets && needsReviewCount > 0) {
         return (
            <Badge
               key={item.path}
               badgeContent={needsReviewCount}
               color='success'
               overlap='rectangular'
               // Default top-right anchor sits at the Button's outer edge,
               // which includes its horizontal px padding — with the next
               // nav item right next door that reads as "floating between
               // the two labels" rather than "on Run Tickets" (Duane,
               // 2026-08-21). Pull it down/left off the corner so it hugs
               // the Run Tickets text instead.
               sx={{
                  '& .MuiBadge-badge': {
                     top: {xs: 4, sm: 5, md: 6},
                     right: {xs: 6, sm: 10, md: 16},
                  },
               }}
            >
               {button}
            </Badge>
         );
      }
      return button;
   };

   // Brand mark / nav-item pills / right-side controls (sync + offline
   // chips, date picker, account menu button) split into standalone JSX
   // so the two layouts below (single row when everything fits, 2 rows
   // when it doesn't — see `device.isSmallViewport` below) can share them
   // instead of duplicating each block.
   const brandSection = (
      <Stack direction='row' sx={{alignItems: 'center', gap: 1}}>
         <Box
            sx={{
               width: 32,
               height: 32,
               borderRadius: '50%',
               bgcolor: 'background.paper',
               color: 'primary.contrastText',
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               fontWeight: 800,
            }}
         >
            <img alt={'logo'} src={'/images/logo-small.png'} width={32} height={32} />
         </Box>
         {/* Both always shown now — previously hidden below sm/md to
             save room, but the brand row no longer needs to share space
             with the nav items (those get their own row below `md` now,
             see the isSmallViewport branch below), so there's room for
             both down to the narrowest widths this gets tested at
             (Duane, 2026-08-26: "we don't need to ever remove Warrior"
             / "leave Production... if it fits, leave it" — this fits at
             the ~490px width from Duane's own test screenshots, with
             room to spare; flag if a narrower real-world window ever
             disagrees). */}
         <Typography variant='h6' sx={{fontWeight: 700, whiteSpace: 'nowrap'}}>
            WARRIOR
         </Typography>
         <Typography variant='subtitle2' sx={{fontWeight: 700, marginTop: '3px', whiteSpace: 'nowrap'}}>
            PRODUCTION
         </Typography>
      </Stack>
   );

   const navSection = (
      <>
         {navItems.map(renderNavButton)}
         {isAdmin && (
            <>
               {renderNavDropdown('Reports', REPORTS_MENU_ITEMS, reportsMenuAnchor, setReportsMenuAnchor)}
               {renderNavDropdown('Setup', SETUP_MENU_ITEMS, setupMenuAnchor, setSetupMenuAnchor)}
            </>
         )}
      </>
   );

   const controlsSection = (
      <>
         {queue.length > 0 && (
            <Chip
               icon={<SyncIcon sx={{animation: isSyncing ? 'spin 1.2s linear infinite' : 'none'}} />}
               label={isSyncing ? `Syncing ${queue.length}…` : `${queue.length} pending sync`}
               color={syncState === 'error' ? 'error' : 'default'}
               size='small'
               sx={{
                  mr: 1,
                  '@keyframes spin': {from: {transform: 'rotate(0deg)'}, to: {transform: 'rotate(360deg)'}},
               }}
            />
         )}
         {/* ADDED 2026-08-28 (Duane, per the App-Development Plan's offline
             requirement: "If no network connection detected, display
             offline mode indicator. Data entered offline queues for sync.")
             — the second half was already true (see the pending-sync Chip
             above and the offline queue it reflects), but there was no
             general always-visible "you're offline right now" indicator
             anywhere in the app before this: the only existing offline
             Chip below only ever reflected the auth state from app
             bootstrap (status === 'offline-cached'), which is set once at
             startup and never re-evaluated — it could still read
             'offline-cached' long after connectivity came back, and had
             nothing to say if you were fully authenticated and only LATER
             lost connection mid-session. This one tracks live isOnline
             instead, so it actually reflects "no network connection
             detected" as the requirement asks. */}
         {!isOnline && <Chip icon={<WifiOffIcon />} label='Offline' color='warning' size='small' sx={{mr: 2}} />}
         {/* Only shown once back online — while still offline, the Chip
             above already says so, and "cached session" on top of that is
             redundant. Otherwise unchanged from before. */}
         {status === 'offline-cached' && isOnline && (
            <Chip icon={<WifiOffIcon />} label='Offline — cached session' color='warning' size='small' sx={{mr: 2}} />
         )}
         {/* Permanently disabled, not a TODO (Duane, 2026-08-31): this was
             originally meant to let Admin/SuperAdmin page back to a
             different date's data, but every report screen now has its
             own date selection, so a global override here isn't needed.
             The date itself stays visible on desktop as a read-only
             label (see ResponsiveDatePicker's `disabled` prop) — it's
             only the calendar picker/edit affordance that's off. */}
         <ResponsiveDatePicker dateAtom={reportDateAtom} disabled={true} />
         <ButtonBase
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
               display: 'flex',
               alignItems: 'center',
               gap: 1,
               borderRadius: 2,
               px: 1,
               py: 0.5,
               '&:hover': {bgcolor: 'action.hover'},
            }}
         >
            <Box
               sx={{
                  width: 28,
                  height: 28,
                  flexShrink: 0,
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
               }}
            >
               {initials || <AccountCircleIcon fontSize='small' />}
            </Box>
            <Typography
               variant='body2'
               sx={{fontWeight: 600, display: {xs: 'none', md: 'block'}, whiteSpace: 'nowrap'}}
            >
               {displayName}
            </Typography>
            <KeyboardArrowDownIcon fontSize='small' sx={{color: 'text.secondary'}} />
         </ButtonBase>
      </>
   );

   const accountMenu = (
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
         {THEME_OPTIONS.map((opt) => (
            <MenuItem
               key={opt.value}
               selected={themePreference === opt.value}
               onClick={() => setThemePreference(opt.value)}
            >
               <ListItemIcon>{opt.icon}</ListItemIcon>
               <ListItemText>{opt.label}</ListItemText>
               {themePreference === opt.value && <CheckIcon fontSize='small' sx={{ml: 2, color: 'primary.main'}} />}
            </MenuItem>
         ))}

         <Divider />

         <MenuItem
            onClick={() => {
               setMenuAnchor(null);
               void signOut();
            }}
         >
            Sign out
         </MenuItem>
      </Menu>
   );

   // Single row when the nav items fit alongside the brand mark and the
   // right-side controls; 2 rows (nav gets its own full-width line below)
   // only once they don't. `device.isSmallViewport` is the same
   // <=1070px signal already used above to shrink the nav buttons
   // themselves (see `renderNavButton`/`renderNavDropdown`) — reusing it
   // here instead of a second breakpoint keeps "things are getting tight"
   // as one signal instead of two that could drift apart (Duane,
   // 2026-08-26: wanted the 2-row layout ONLY when the nav buttons don't
   // fit on the brand/account row, not unconditionally — the prior fix
   // forced 2 rows always, even at full desktop width with room to
   // spare).
   if (device.isSmallViewport) {
      return (
         <Toolbar sx={{flexWrap: 'wrap', rowGap: 1, minHeight: 72, py: 1}}>
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%', gap: 1}}>
               {brandSection}
               <Box sx={{flexGrow: 1}} />
               {controlsSection}
            </Box>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', width: '100%'}}>
               {navSection}
            </Box>
            {accountMenu}
         </Toolbar>
      );
   }

   return (
      <Toolbar sx={{gap: 1, minHeight: 72}}>
         <Box sx={{mr: 3}}>{brandSection}</Box>
         <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center'}}>{navSection}</Box>
         <Box sx={{flexGrow: 1}} />
         {controlsSection}
         {accountMenu}
      </Toolbar>
   );
}
